import React, { useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Self-service GDPR page — Right to data portability (export) and
 * Right to erasure (delete). Linked from the footer + profile settings.
 */
const AccountPrivacyPage = () => {
  const { user, getAuthToken, logout } = useAuth();
  const navigate = useNavigate();

  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');

  const [showDelete, setShowDelete] = useState(false);
  const [pw, setPw] = useState('');
  const [confirmWord, setConfirmWord] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  if (!user) {
    return (
      <div className="min-h-screen bg-[#F4F2E9] flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center px-4 py-12">
          <div className="max-w-md text-center">
            <p className="text-slate-700 mb-4">Please sign in to access your privacy controls.</p>
            <Link to="/login" className="inline-block bg-[#1F3A68] text-white px-6 py-2.5 rounded-lg font-semibold hover:bg-[#1F3A68]/90">Sign in</Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const handleExport = async () => {
    setExportError('');
    setExporting(true);
    try {
      const res = await axios.get(`${API_URL}/api/users/export-data`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ariv-data-export-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setExportError(err?.response?.data?.detail || 'Could not export your data.');
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async (e) => {
    e.preventDefault();
    setDeleteError('');
    if (confirmWord.trim().toUpperCase() !== 'DELETE') {
      setDeleteError('Please type DELETE to confirm.');
      return;
    }
    setDeleting(true);
    try {
      await axios.post(
        `${API_URL}/api/users/delete-account`,
        { password: pw, confirmation: confirmWord },
        { headers: { Authorization: `Bearer ${getAuthToken()}` } }
      );
      // Clear session locally and redirect.
      try { await logout?.(); } catch {}
      navigate('/');
      setTimeout(() => {
        alert('Your account has been deleted. We hope to see you again.');
      }, 200);
    } catch (err) {
      setDeleteError(err?.response?.data?.detail || 'Could not delete account.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F2E9] flex flex-col">
      <Header />
      <main className="flex-1 container mx-auto px-4 py-10 max-w-3xl">
        <h1 className="text-2xl md:text-3xl font-bold text-[#1F3A68] mb-2">Privacy &amp; your data</h1>
        <p className="text-slate-600 mb-6">
          Under UK GDPR you have the right to export a copy of your data and to request deletion of your account.
          See our <Link to="/privacy" className="underline">Privacy Policy</Link> for more detail.
        </p>

        {/* ---- Export ---- */}
        <section className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 mb-6">
          <h2 className="text-xl font-bold text-[#1F3A68] mb-2">Export your data</h2>
          <p className="text-sm text-slate-600 mb-4">
            Download a JSON file containing your profile, bookings, orders, reviews, messages, and financial records held on Ariv.
            The file is for your personal use — keep it safe because it contains personal information.
          </p>
          <button
            onClick={handleExport}
            disabled={exporting}
            className="bg-[#1F3A68] text-white px-5 py-2.5 rounded-lg font-semibold hover:bg-[#1F3A68]/90 disabled:opacity-50"
          >
            {exporting ? 'Preparing download…' : 'Download my data (JSON)'}
          </button>
          {exportError && <p className="text-sm text-red-600 mt-3">{exportError}</p>}
        </section>

        {/* ---- Delete ---- */}
        <section className="bg-white rounded-2xl shadow-sm border-2 border-red-200 p-6">
          <h2 className="text-xl font-bold text-red-700 mb-2">Delete my account</h2>
          <p className="text-sm text-slate-600 mb-2">
            Deleting your account is permanent. Your personal information will be removed or anonymised within 30 days.
          </p>
          <p className="text-sm text-slate-500 mb-4">
            Financial records attached to bookings, orders, rides, or payouts are retained for 6 years under HMRC rules — this is required by law and is explained in our Privacy Policy.
          </p>

          {!showDelete ? (
            <button
              onClick={() => setShowDelete(true)}
              className="bg-red-600 text-white px-5 py-2.5 rounded-lg font-semibold hover:bg-red-700"
            >
              I want to delete my account
            </button>
          ) : (
            <form onSubmit={handleDelete} className="space-y-3">
              <div>
                <label htmlFor="pw" className="block text-sm font-medium text-slate-700 mb-1">Your current password</label>
                <input
                  id="pw"
                  type="password"
                  required
                  autoComplete="current-password"
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  className="w-full h-11 border border-slate-300 rounded-lg px-3 focus:outline-none focus:ring-2 focus:ring-red-500/40"
                />
              </div>
              <div>
                <label htmlFor="confirm" className="block text-sm font-medium text-slate-700 mb-1">
                  Type <code className="font-mono bg-slate-100 px-1.5 py-0.5 rounded">DELETE</code> to confirm
                </label>
                <input
                  id="confirm"
                  type="text"
                  required
                  value={confirmWord}
                  onChange={(e) => setConfirmWord(e.target.value)}
                  className="w-full h-11 border border-slate-300 rounded-lg px-3 focus:outline-none focus:ring-2 focus:ring-red-500/40"
                />
              </div>
              {deleteError && <p className="text-sm text-red-600">{deleteError}</p>}
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={deleting || !pw || confirmWord.trim().toUpperCase() !== 'DELETE'}
                  className="bg-red-600 text-white px-5 py-2.5 rounded-lg font-semibold hover:bg-red-700 disabled:opacity-50"
                >
                  {deleting ? 'Deleting…' : 'Permanently delete my account'}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowDelete(false); setPw(''); setConfirmWord(''); setDeleteError(''); }}
                  className="px-5 py-2.5 rounded-lg font-semibold text-slate-700 border border-slate-300 hover:bg-slate-100"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default AccountPrivacyPage;
